import { Vulnerability, VulnerabilityId } from '../Vulnerability';
import { State, Action, Actions } from './store';

enum Progress {
  START = 'Start',
  FINISH = 'Finish',
}

const initialState: State = {
  0: {
    error: null,
    isLoading: false,
    vulnerabilityItems: [],
  },
  1: {
    error: null,
    isLoading: false,
    vulnerabilityItems: [],
  },
};

const setGettingVulnetabilitiesStart = (state: State[0 | 1], action: Action): State[0 | 1] => ({
  ...state,
  isLoading: true,
});

const setGettingVulnetabilitiesFinish = (state: State[0 | 1], action: Action): State[0 | 1] => ({
  ...state,
  isLoading: false,
  vulnerabilityItems: [
    ...(action.payload as Vulnerability[]).map((vulnerability) => ({
      isLoading: false,
      error: null,
      vulnerability,
    })),
  ],
});

const setGettingVulnetabilitiesError = (state: State[0 | 1], action: Action): State[0 | 1] => ({
  ...state,
  error: action.payload as string,
  isLoading: false,
});

const addVulnerabilityFinish = (state: State[0 | 1], action: Action): State[0 | 1] => ({
  ...state,
  vulnerabilityItems: [
    ...state.vulnerabilityItems,
    {
      error: null,
      isLoading: false,
      vulnerability: action.payload as Vulnerability,
    },
  ],
});

const deleteVulnerabilityFinish = (state: State[0 | 1], action: Action): State[0 | 1] => {
  const foundVulnerabilityIndex = state.vulnerabilityItems.findIndex(
    (vulnerability) => vulnerability.vulnerability.id === (action.payload as VulnerabilityId),
  );
  const copyOfVulnerabilities = [...state.vulnerabilityItems];

  if (foundVulnerabilityIndex !== -1) {
    copyOfVulnerabilities.splice(foundVulnerabilityIndex, 1);
  }

  return {
    ...state,
    vulnerabilityItems: copyOfVulnerabilities,
  };
};

const deleteVulnerabilityStart = (state: State[0 | 1], action: Action): State[0 | 1] => {
  const foundVulnerabilityIndex = state.vulnerabilityItems.findIndex(
    (vulnerability) => vulnerability.vulnerability.id === (action.payload as VulnerabilityId),
  );
  const copyOfVulnerabilities = [...state.vulnerabilityItems];

  if (foundVulnerabilityIndex !== -1) {
    copyOfVulnerabilities[foundVulnerabilityIndex].isLoading = true;
  }

  return {
    ...state,
    vulnerabilityItems: copyOfVulnerabilities,
  };
};

const deleteVulnerabilityError = (state: State[0 | 1], action: Action): State[0 | 1] => {
  const foundVulnerabilityIndex = state.vulnerabilityItems.findIndex(
    (vulnerability) => vulnerability.vulnerability.id === (action.payload as VulnerabilityId),
  );
  const copyOfVulnerabilities = [...state.vulnerabilityItems];

  if (foundVulnerabilityIndex !== -1) {
    copyOfVulnerabilities[foundVulnerabilityIndex].error = 'Could not delete vulnerability.';
  }

  return {
    ...state,
    vulnerabilityItems: copyOfVulnerabilities,
  };
};

const vulnerabilityInProgress = (
  state: State[0 | 1],
  vulnerabilityId: VulnerabilityId,
  progress: Progress,
): State[0 | 1] => {
  const foundVulnerabilityIndex = state.vulnerabilityItems.findIndex(
    (vulnerability) => vulnerability.vulnerability.id === vulnerabilityId,
  );
  const copyOfVulnerabilities = [...state.vulnerabilityItems];

  if (foundVulnerabilityIndex !== -1) {
    copyOfVulnerabilities[foundVulnerabilityIndex].isLoading = progress === Progress.START;
  }

  return {
    ...state,
    vulnerabilityItems: copyOfVulnerabilities,
  };
};

const vulnerabilityUpdateFinish = (state: State[0 | 1], updatedVulnerability: Vulnerability): State[0 | 1] => {
  const foundVulnerabilityIndex = state.vulnerabilityItems.findIndex(
    (vulnerability) => vulnerability.vulnerability.id === updatedVulnerability.id,
  );
  const copyOfVulnerabilities = [...state.vulnerabilityItems];

  if (foundVulnerabilityIndex !== -1) {
    copyOfVulnerabilities[foundVulnerabilityIndex].vulnerability = updatedVulnerability;
  }

  return {
    ...state,
    vulnerabilityItems: copyOfVulnerabilities,
  };
};

const reducerFunc = (state: State = initialState, action: Action) => {
  const map = new Map([
    /*
     * SET VULNERABILITIES
     * APPLICANT 1
     */
    [
      Actions.SET_APPLICANT_1_VULNERABILITIES_START,
      () => ({
        ...state,
        0: setGettingVulnetabilitiesStart(state[0], action),
      }),
    ],
    [
      Actions.SET_APPLICANT_1_VULNERABILITIES_FINISH,
      () => ({
        ...state,
        0: setGettingVulnetabilitiesFinish(state[0], action),
      }),
    ],
    [
      Actions.SET_APPLICANT_1_VULNERABILITIES_ERROR,
      () => ({
        ...state,
        0: setGettingVulnetabilitiesError(state[0], action),
      }),
    ],

    /*
     * SET VULNERABILITIES
     * APPLICANT 2
     */
    [
      Actions.SET_APPLICANT_2_VULNERABILITIES_START,
      () => ({
        ...state,
        1: setGettingVulnetabilitiesStart(state[1], action),
      }),
    ],
    [
      Actions.SET_APPLICANT_2_VULNERABILITIES_FINISH,
      () => ({
        ...state,
        1: setGettingVulnetabilitiesFinish(state[1], action),
      }),
    ],
    [
      Actions.SET_APPLICANT_2_VULNERABILITIES_ERROR,
      () => ({
        ...state,
        1: setGettingVulnetabilitiesError(state[1], action),
      }),
    ],

    /*
     * ADD VULNERABILITY
     * APPLICANT 1
     */
    [
      Actions.ADD_APPLICANT_1_VULNERABILITY_FINISH,
      () => ({
        ...state,
        0: addVulnerabilityFinish(state[0], action),
      }),
    ],

    /*
     * ADD VULNERABILITY
     * APPLICANT 1
     */
    [
      Actions.ADD_APPLICANT_2_VULNERABILITY_FINISH,
      () => ({
        ...state,
        1: addVulnerabilityFinish(state[1], action),
      }),
    ],

    /*
     * DELETE VULNERABILITY
     * APPLICANT 1
     */
    [
      Actions.DELETE_APPLICANT_1_VULNERABILITY_START,
      () => ({
        ...state,
        0: deleteVulnerabilityStart(state[0], action),
      }),
    ],
    [
      Actions.DELETE_APPLICANT_1_VULNERABILITY_FINISH,
      () => ({
        ...state,
        0: deleteVulnerabilityFinish(state[0], action),
      }),
    ],
    [
      Actions.DELETE_APPLICANT_1_VULNERABILITY_ERROR,
      () => ({
        ...state,
        0: deleteVulnerabilityError(state[0], action),
      }),
    ],

    /*
     * DELETE VULNERABILITY
     * APPLICANT 2
     */
    [
      Actions.DELETE_APPLICANT_2_VULNERABILITY_START,
      () => ({
        ...state,
        1: deleteVulnerabilityStart(state[1], action),
      }),
    ],
    [
      Actions.DELETE_APPLICANT_2_VULNERABILITY_FINISH,
      () => ({
        ...state,
        1: deleteVulnerabilityFinish(state[1], action),
      }),
    ],
    [
      Actions.DELETE_APPLICANT_2_VULNERABILITY_ERROR,
      () => ({
        ...state,
        1: deleteVulnerabilityError(state[1], action),
      }),
    ],

    /*
     * VULNERABILITY IN PROGRESS
     * APPLICANT 1
     */
    [
      Actions.APPLICANT_1_VULNERABILITY_IN_PROGRESS_START,
      () => ({
        ...state,
        0: vulnerabilityInProgress(state[0], action.payload as VulnerabilityId, Progress.START),
      }),
    ],
    [
      Actions.APPLICANT_1_VULNERABILITY_IN_PROGRESS_FINISH,
      () => ({
        ...state,
        0: vulnerabilityInProgress(state[0], action.payload as VulnerabilityId, Progress.FINISH),
      }),
    ],
    /*
     * VULNERABILITY IN PROGRESS
     * APPLICANT 2
     */
    [
      Actions.APPLICANT_2_VULNERABILITY_IN_PROGRESS_START,
      () => ({
        ...state,
        1: vulnerabilityInProgress(state[1], action.payload as VulnerabilityId, Progress.START),
      }),
    ],
    [
      Actions.APPLICANT_2_VULNERABILITY_IN_PROGRESS_FINISH,
      () => ({
        ...state,
        1: vulnerabilityInProgress(state[1], action.payload as VulnerabilityId, Progress.FINISH),
      }),
    ],

    /*
     * VULNERABILITY UPDATE FINISH
     * APPLICANT 1
     */
    [
      Actions.UPDATE_APPLICANT_1_VULNERABILITY_FINISH,
      () => ({
        ...state,
        0: vulnerabilityUpdateFinish(state[0], action.payload as Vulnerability),
      }),
    ],
    /*
     * VULNERABILITY UPDATE FINISH
     * APPLICANT 2
     */
    [
      Actions.UPDATE_APPLICANT_2_VULNERABILITY_FINISH,
      () => ({
        ...state,
        1: vulnerabilityUpdateFinish(state[1], action.payload as Vulnerability),
      }),
    ],
  ]);

  const actionCallback = map.get(action.type);

  return actionCallback && typeof actionCallback === 'function' ? actionCallback() : state;
};

export default reducerFunc;
